import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp,
  DollarSign,
  BarChart3,
  Eye,
  ShoppingCart,
  Menu,
  Clock,
  Target,
  Zap,
} from "lucide-react";
import { useSelector } from "react-redux";
import dealsService from "../../components/services/DealsService";
import { toast } from "react-toastify";
import AuthenticatedNavbar from "../../components/AuthenticatedNavbar";
import DashboardSidebar from "../../components/DashboardSidebar";
import Loader from "../../components/Loader";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import dashboardService from "../../components/services/DashboardService";

const Dashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const [recentDeals, setRecentDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("dashboard");
  const [dashboardStats, setDashboardStats] = useState(null);
  const[performanceStats, setPerformanceStats] = useState(null);

  useEffect(() => {
    const fetchRecentDeals = async () => {
      setIsLoading(true);
      try {
        const [response, dashboardStatsResponse] = await Promise.all([
          dealsService.getTrendingDeals(8),
          dashboardService.getDashboardStats(),
        ]);
        if (response?.data?.status_code === "000") {
          setRecentDeals(response?.data?.data);
        } else {
          toast.error(
            response?.data?.reason || "Sorry, Could not fetch recent deals"
          );
        }
        if (dashboardStatsResponse?.data?.status_code === "000") {
          setDashboardStats(dashboardStatsResponse?.data?.data);
          setPerformanceStats(dashboardStatsResponse?.data?.data?.performance);
        } else {
          toast.error(
            dashboardStatsResponse?.data?.reason ||
              "Sorry, Could not fetch dashboard stats"
          );
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.reason || "Sorry, An Error Occurred"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchRecentDeals();
  }, []);

  console.log("Dashboard Stats", dashboardStats);

  // Mock data - replace with actual API calls
  const stats = {
    totalEarnings: "GH₵0",
    monthlyEarnings: "GH₵0",
    totalClicks: "0",
    conversionRate: "0%",
    activeDeals: 0,
    totalCommissions: 0,
  };

  // Empty state for recent earnings
  const recentEarnings = [];

  // Chart data for doughnut chart - using real performance data
  const getChartData = () => {
    if (!performanceStats || Object.keys(performanceStats).length === 0) {
      return [
        { name: "No Data", value: 100, color: "#E5E7EB" }
      ];
    }

    const colors = ["#F2640F", "#FF0000", "#FF6B6B", "#FF8E53", "#FFB347"];
    const entries = Object.entries(performanceStats);
    
    // Calculate total for percentage calculation
    const total = entries.reduce((sum, [, value]) => sum + value, 0);
    
    return entries.map(([category, value], index) => ({
      name: category,
      value: total > 0 ? Math.round((value / total) * 100) : 0,
      actualValue: value,
      color: colors[index % colors.length]
    }));
  };

  const chartData = getChartData();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <DashboardSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        user={user}
      />

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? "lg:ml-80" : ""}`}
      >
        <AuthenticatedNavbar />

        <div className="px-4 py-20 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Sidebar Toggle Button */}
          {!sidebarOpen && (
            <div className="mb-6">
              <button
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-2 px-4 py-2 text-[#F2640F] bg-[#F2640F]/10 rounded-lg border border-[#F2640F]/20 hover:bg-[#F2640F]/20 transition-colors"
              >
                <Menu className="w-5 h-5" />
                Open Sidebar
              </button>
            </div>
          )}

          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 gap-4 mb-8 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
          >
            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Earnings
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.totalEarnings}
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-500">Coming Soon</span>
                  </div>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-green-100 rounded-xl">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Monthly Earnings
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.monthlyEarnings}
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-500">Coming Soon</span>
                  </div>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-[#F2640F]/10 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-[#F2640F]" />
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Pending Redemption
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats?.overall?.total_redeemed}
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-500">
                      Successful redemption(s) from links
                    </span>
                  </div>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-[#FF0000]/10 rounded-xl">
                  <Eye className="w-6 h-6 text-[#FF0000]" />
                </div>
              </div>
            </div>

            <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    Total Completed
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {dashboardStats?.overall?.total_completed}
                  </p>
                  <div className="flex items-center mt-2">
                    <span className="text-sm text-gray-500">
                      have redeemed their codes from links
                    </span>
                  </div>
                </div>
                <div className="flex justify-center items-center w-12 h-12 bg-[#F2640F]/10 rounded-xl">
                  <BarChart3 className="w-6 h-6 text-[#F2640F]" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recent Deals Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-center">
              <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                Find new product to promote
              </h2>
              {/* <div className="flex gap-2 items-center">
                <select className="w-full sm:w-auto px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F2640F] focus:border-transparent">
                  {categoryOptions.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label.charAt(0).toUpperCase() +
                        category.label.slice(1)}
                    </option>
                  ))}
                </select>
              </div> */}
            </div>

            {/* Main Content Grid - 2/3 Products + 1/3 Chart */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Products Section - 2/3 width */}
              <div className="order-2 lg:col-span-2 lg:order-1">
                <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm">
                  {/* Header */}
                  <div className="flex bg-gray-50 border-b border-gray-200">
                    <div className="flex-1 px-4 py-4 sm:px-6">
                      <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                        Products
                      </h3>
                    </div>
                    <div className="px-4 py-4 w-24 border-l border-gray-200 sm:px-6 sm:w-32">
                      <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                        Earn
                      </h3>
                    </div>
                  </div>

                  {/* Products List */}
                  <div className="divide-y divide-gray-200">
                    {recentDeals &&
                      recentDeals.slice(0, 4).map((deal, index) => (
                        <div
                          key={deal.id}
                          className="flex items-center p-4 transition-colors sm:p-6 hover:bg-gray-50"
                        >
                          {/* Product Info */}
                          <div className="flex flex-1 gap-3 items-center min-w-0 sm:gap-4">
                            {/* Product Image */}
                            <div className="overflow-hidden relative flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg sm:w-16 sm:h-16">
                              {deal.image ? (
                                <img
                                  src={deal.image}
                                  alt={deal.title}
                                  className="object-cover w-full h-full"
                                />
                              ) : (
                                <div className="flex justify-center items-center w-full h-full bg-gray-200">
                                  <ShoppingCart className="w-4 h-4 text-gray-400 sm:w-6 sm:h-6" />
                                </div>
                              )}
                            </div>

                            {/* Product Details */}
                            <div className="flex-1 min-w-0">
                              <div className="flex gap-2 items-center mb-1">
                                <span className="text-xs font-medium text-gray-500 truncate sm:text-sm">
                                  {deal.company_name || "Unknown Store"}
                                </span>
                              </div>
                              <h4 className="mb-1 text-xs font-medium text-gray-900 sm:text-sm line-clamp-2">
                                {deal.title}
                              </h4>
                              <div className="flex gap-2 items-center mb-1">
                                <span className="text-xs font-semibold text-gray-900 sm:text-sm">
                                  GH₵
                                  {deal.discounted_price || deal.original_price}
                                </span>
                                {deal.discount_percentage && (
                                  <span className="text-xs text-red-500 line-through sm:text-sm">
                                    GH₵{deal.original_price}
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-col gap-1 items-start text-xs text-gray-500 sm:flex-row sm:gap-4 sm:items-center">
                              
                                <div className="flex gap-1 items-center">
                                  <div className="w-2 h-2 bg-red-500 rounded-full sm:w-3 sm:h-3"></div>
                                  <span>
                                    {(() => {
                                      if (!deal.expiry_date) return "No expiry";
                                      const expiryDate = new Date(deal.expiry_date);
                                      const today = new Date();
                                      const diffTime = expiryDate - today;
                                      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                      
                                      if (diffDays <= 0) return "Expired";
                                      if (diffDays === 1) return "Expires today";
                                      return `Expires in ${diffDays} days`;
                                    })()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Earn Section */}
                          <div className="flex flex-col gap-2 items-center w-20 sm:gap-3 sm:w-24 lg:w-32">
                            <Link
                              to={`/deal-authenticated/${deal.deal_slug}`}
                              className="w-full px-2 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white text-xs sm:text-sm font-medium rounded-lg hover:shadow-md transition-all duration-200 text-center block"
                            >
                              Apply
                            </Link>
                          </div>
                        </div>
                      ))}
                  </div>

                  {/* Footer */}
                  <div className="px-4 py-4 bg-gray-50 border-t border-gray-200 sm:px-6">
                    <Link
                      to="/trending"
                      className="text-sm text-[#F2640F] hover:text-[#FF0000] font-medium"
                    >
                      View All Deals →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Chart Section - 1/3 width */}
              <div className="order-1 lg:col-span-1 lg:order-2">
                <div className="relative p-4 bg-white rounded-xl border border-gray-100 shadow-sm sm:p-6">
                  {/* Chart Header */}
                  <div className="flex justify-between items-center mb-4 sm:mb-6">
                    <div className="flex gap-2 items-center">
                      <div className="p-1.5 sm:p-2 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-lg">
                        <Target className="w-4 h-4 text-white sm:w-5 sm:h-5" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-900 sm:text-lg">
                        Performance
                      </h3>
                    </div>
                    <div className="px-2 py-1 bg-gradient-to-r from-orange-100 to-red-100 rounded-full sm:px-3">
                      <span className="text-xs font-medium text-[#F2640F]">
                        {performanceStats && Object.keys(performanceStats).length > 0 
                          ? "Live Data" 
                          : "Coming Soon"}
                      </span>
                    </div>
                  </div>

                  {/* Doughnut Chart */}
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie
                          data={chartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={70}
                          outerRadius={120}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, name, props) => {
                            const actualValue = props.payload.actualValue;
                            return [`${actualValue} interactions (${value}%)`, props.payload.name];
                          }}
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e5e7eb",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>

                    {/* Center Text */}
                    <div className="flex absolute inset-0 justify-center items-center pointer-events-none">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-gray-900">
                          {performanceStats && Object.keys(performanceStats).length > 0 
                            ? Object.values(performanceStats).reduce((sum, value) => sum + value, 0)
                            : 0}
                        </div>
                        <div className="text-sm text-gray-500">
                          Total Interactions
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chart Legend */}
                  <div className="mt-4 space-y-2 sm:mt-6">
                    {chartData.map((item, index) => (
                      <div
                        key={index}
                        className="flex justify-between items-center"
                      >
                        <div className="flex gap-2 items-center min-w-0">
                          <div
                            className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: item.color }}
                          ></div>
                          <span className="text-xs text-gray-600 truncate sm:text-sm">
                            {item.name}
                          </span>
                        </div>
                        <span className="flex-shrink-0 text-xs font-medium text-gray-900 sm:text-sm">
                          {item.actualValue ? `${item.actualValue} (${item.value}%)` : `${item.value}%`}
                        </span>
                      </div>
                    ))}
                  </div>

                </div>
              </div>
            </div>
          </motion.div>

          {/* Recently Approved Deals */}
          {dashboardStats?.latest_deals && dashboardStats.latest_deals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="py-16 mb-8"
            >
              <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-center">
                <div className="flex gap-3 items-center">
                  <div className="p-2 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-lg">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Recently Approved Deals
                  </h2>
                  <span className="px-3 py-1 text-xs font-medium text-white bg-green-500 rounded-full">
                    {dashboardStats.latest_deals.length} New
                  </span>
                </div>
                <Link
                  to="/applied-deals"
                  className="text-sm text-[#F2640F] hover:text-[#FF0000] font-medium"
                >
                  View All →
                </Link>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {dashboardStats.latest_deals.map((deal) => (
                  <motion.div
                    key={deal.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.1 }}
                    className="overflow-hidden relative bg-white rounded-2xl border border-gray-100 shadow-sm transition-all duration-300 group hover:shadow-lg"
                  >
                    {/* Status Badge */}
                    <div className="absolute top-4 right-4 z-10">
                      <span className="px-3 py-1 text-xs font-medium text-white bg-green-500 rounded-full shadow-sm">
                        {deal.status}
                      </span>
                    </div>

                    {/* Deal Image */}
                    <div className="overflow-hidden relative h-48">
                      {deal.image ? (
                        <img
                          src={deal.image}
                          alt={deal.title}
                          className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-gray-100 to-gray-200">
                          <ShoppingCart className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t to-transparent from-black/20"></div>
                      
                      {/* Company Logo */}
                      {deal.company_logo && (
                        <div className="absolute bottom-4 left-4">
                          <img
                            src={deal.company_logo}
                            alt={deal.company_name}
                            className="p-1 w-8 h-8 bg-white rounded-lg shadow-sm"
                          />
                        </div>
                      )}
                    </div>

                    {/* Deal Content */}
                    <div className="p-6">
                      {/* Company & Category */}
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-600 truncate">
                            {deal.company_name || "Unknown Store"}
                          </p>
                          {deal.category && deal.category.length > 0 && (
                            <span className="inline-block px-2 py-1 mt-1 text-xs font-medium text-[#F2640F] bg-[#F2640F]/10 rounded-full">
                              {deal.category[0]}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Deal Title */}
                      <h3 className="mb-3 text-lg font-semibold text-gray-900 line-clamp-2 group-hover:text-[#F2640F] transition-colors">
                        {deal.title}
                      </h3>

                      {/* Pricing */}
                      <div className="flex gap-2 items-center mb-4">
                        <span className="text-xl font-bold text-gray-900">
                          GH₵{deal.discounted_price || deal.price}
                        </span>
                        {deal.discount_percentage && (
                          <span className="text-sm text-red-500 line-through">
                            GH₵{deal.price}
                          </span>
                        )}
                        {deal.discount_percentage && (
                          <span className="px-2 py-1 text-xs font-medium text-white bg-red-500 rounded-full">
                            -{deal.discount_percentage}%
                          </span>
                        )}
                      </div>

                      {/* Deal Stats */}
                      <div className="flex justify-between items-center mb-4 text-sm text-gray-500">
                        <div className="flex gap-4">
                          <span className="flex gap-1 items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            {deal.redeemed_count || 0} redeemed
                          </span>
                          <span className="flex gap-1 items-center">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            {deal.completed_count || 0} completed
                          </span>
                        </div>
                      </div>

                      {/* Expiry Date */}
                      <div className="flex gap-1 items-center mb-4 text-sm text-gray-500">
                        <Clock className="w-4 h-4" />
                        <span>
                          {(() => {
                            if (!deal.expiry_date) return "No expiry";
                            const expiryDate = new Date(deal.expiry_date);
                            const today = new Date();
                            const diffTime = expiryDate - today;
                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                            
                            if (diffDays <= 0) return "Expired";
                            if (diffDays === 1) return "Expires today";
                            return `Expires in ${diffDays} days`;
                          })()}
                        </span>
                      </div>

                      {/* Action Button */}
                      <Link
                        to={`/deal-authenticated/${deal.deal_slug}`}
                        className="w-full flex justify-center items-center gap-2 px-4 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-medium rounded-xl hover:shadow-lg transition-all duration-300 group-hover:scale-105"
                      >
                        <Target className="w-4 h-4" />
                        Promote Deal
                      </Link>
                    </div>

                    {/* Hover Effect Border */}
                    <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#F2640F]/20 transition-colors duration-300 pointer-events-none"></div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Recent Earnings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
          >
            <div className="flex justify-between items-center mb-6">
              <div className="flex gap-3 items-center">
                <h2 className="text-xl font-semibold text-gray-900">
                  Recent Earnings
                </h2>
                <span className="px-3 py-1 text-xs font-medium text-white bg-[#F2640F] rounded-full">
                  Coming Soon
                </span>
              </div>
              <Link
                to="/earnings"
                className="text-sm text-[#F2640F] hover:text-[#FF0000]"
              >
                View All
              </Link>
            </div>

            {recentEarnings.length > 0 ? (
              <div className="space-y-4">
                {recentEarnings.map((earning) => (
                  <div
                    key={earning.id}
                    className="flex justify-between items-center p-3 bg-gray-50 rounded-xl"
                  >
                    <div className="flex gap-3 items-center">
                      <div>
                        <p className="font-medium text-gray-900">
                          {earning.deal}
                        </p>
                        <p className="text-sm text-gray-500">{earning.date}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {earning.amount}
                      </p>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          earning.status === "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                        }`}
                      >
                        {earning.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Empty State */
              <div className="flex flex-col justify-center items-center py-12 text-center">
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="flex justify-center items-center w-20 h-20 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full">
                    <DollarSign className="w-10 h-10 text-gray-400" />
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-[#F2640F] to-[#FF0000] rounded-full opacity-20"></div>
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full opacity-20"></div>
                </div>

                {/* Content */}
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  Earnings tracking coming soon
                </h3>
                <p className="mb-6 max-w-md text-gray-600">
                  We're working on implementing earnings tracking! Soon you'll
                  be able to see your commission earnings and track your
                  performance in real-time.
                </p>

                {/* Call to Action */}
                <div className="flex flex-col gap-3 sm:flex-row">
                  <Link
                    to="/trending"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-xl font-semibold transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    <ShoppingCart className="w-4 h-4" />
                    Browse Deals
                  </Link>
                  <Link
                    to="/earnings"
                    className="inline-flex gap-2 items-center px-6 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl transition-all duration-300 hover:bg-gray-200 hover:shadow-md"
                  >
                    <BarChart3 className="w-4 h-4" />
                    View Analytics
                  </Link>
                </div>

                {/* Coming Soon Info */}
                <div className="p-4 mt-8 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-100">
                  <h4 className="mb-2 text-sm font-semibold text-gray-900">
                    🚀 What's Coming Soon
                  </h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li>• Real-time earnings tracking and analytics</li>
                    <li>• Commission history and payout management</li>
                    <li>• Performance metrics and conversion tracking</li>
                    <li>• Detailed reporting and insights dashboard</li>
                  </ul>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
